import { inject, Injectable } from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, Validators } from '@angular/forms';
import { createSelector, Store } from '@ngrx/store';
import { addDays, addYears, isSameYear } from 'date-fns';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { setDueDateTime } from '@ninety/mobile/pages-shared/to-dos-shared';
import {
  Attachment,
  AttachmentParentType,
  RockLanguage,
  TeamListModel,
  UserListModel,
  UserViewModel,
} from '@ninety/mobile/platform/api';
import { AttachmentsComponent } from '@ninety/mobile/platform/components';
import { getUserName } from '@ninety/mobile/platform/services';
import {
  applyToolStateToForm,
  CompanyUserSelectors,
  CurrentUserSelectors,
  CurrentUserTeamSelectors,
  extractValueFromStore,
  LanguageSelectors,
  selectFiscalCalendar,
  UniversalCreateActions,
  selectRockToolState,
  UserListSelectors,
} from '@ninety/mobile/platform/state';

import { CreateRockVm, InitialFormValues, RockQuarterSelection } from '../../_state/models/create-rock-form.model';
import { RockLevelCode } from '../../_state/models/level-code';
import { CreateRockRequestModel, RockQuarter } from '../../_state/models/rock-request.model';
import {
  RockStatusCode,
  RockStatusCodeClassNameDisplayMap,
  RockStatusCodeDisplayMap,
  RockStatusCodeIconDisplayMap,
} from '../../_state/models/status-code';
import { RockActions } from '../../_state/rock-state.actions';
import { RocksApiService } from '../../services/rocks-api.service';

@Injectable()
export class CreateRockStore {
  private readonly store = inject(Store);
  private readonly rocksApi = inject(RocksApiService);

  readonly form = new FormGroup({
    title: new FormControl<string>(InitialFormValues.title, Validators.required),
    description: new FormControl<string>(InitialFormValues.description),
    teamId: new FormControl<string>(InitialFormValues.teamId, Validators.required),
    dueDate: new FormControl<string>(InitialFormValues.dueDate, Validators.required),
    userId: new FormControl<string>(InitialFormValues.userId, Validators.required),
    additionalTeamIds: new FormControl<string[]>(InitialFormValues.additionalTeamIds),
    statusCode: new FormControl<string>(InitialFormValues.statusCode, Validators.required),
    isDepartmentalRock: new FormControl<boolean>(InitialFormValues.isDepartmentalRock),
    isCompanyRock: new FormControl<boolean>(InitialFormValues.isCompanyRock),
    attachments: new FormControl<Attachment[]>(InitialFormValues.attachments),
    quarter: new FormControl<RockQuarterSelection>(InitialFormValues.quarter),
    rockQuarterYearDueDate: new FormControl<Date>(InitialFormValues.rockQuarterYearDueDate),
  });

  private readonly fiscalCalendar = extractValueFromStore(this.store, selectFiscalCalendar);
  private readonly currentUser$ = this.store.select(CurrentUserSelectors.selectUser);
  private readonly language$ = this.store.select(LanguageSelectors.rocksLanguage);
  selectRockUniversalCreateButtonText = createSelector(LanguageSelectors.rocksLanguage, lang => `Create ${lang.item}`);

  private readonly teams$ = this.store.select(CurrentUserTeamSelectors.selectSortedVisibleTeams);
  private readonly users$ = this.store.select(UserListSelectors.selectAll);
  private readonly sltTeamId$ = this.store.select(CompanyUserSelectors.selectCurrentCompanySltTeamId);
  private readonly isSubmitted$ = new BehaviorSubject<boolean>(false);

  public teamUsers: UserListModel[] = [];

  readonly rockStatusDisplayOptions = [
    ...Object.values(RockStatusCode).map(statusCode => ({
      iconSrc: RockStatusCodeIconDisplayMap[statusCode] ?? '',
      className: RockStatusCodeClassNameDisplayMap[statusCode] ?? '',
      statusCode,
      displayValue: RockStatusCodeDisplayMap[statusCode] ?? '',
    })),
  ];

  public rockQuarterOptions: { quarter: RockQuarter; year: number; rockQuarterYearDueDate: Date }[] = [];

  readonly vm$: Observable<CreateRockVm> = combineLatest([
    this.language$,
    this.teams$,
    this.users$,
    this.sltTeamId$,
    this.currentUser$,
    this.isSubmitted$,
    this.form.valueChanges.pipe(startWith(InitialFormValues)),
  ]).pipe(
    distinctUntilChanged(),
    map(([language, teams, users, leadershipTeamId, currentUser, submitted]) => ({
      isValid: !!this.form.valid,
      dateFormatOptions: this.getDateFormatOptions(),
      dateMax: this.getDateMax(),
      language,
      teams,
      users,
      showDepartmentalRock: this.form.controls.teamId.value === leadershipTeamId,
      showCompanyAndDepartmentalRock:
        this.form.controls.teamId.value === leadershipTeamId ||
        (this.form.controls.additionalTeamIds.value?.some(id => id === leadershipTeamId) ?? false),
      rockStatusDisplayOptions: this.rockStatusDisplayOptions,
      userCanEditRockLevelCode: !!(
        currentUser?.isAdminOrOwner ||
        currentUser?.isImplementer ||
        (leadershipTeamId && currentUser?.teams?.some(team => team.teamId === leadershipTeamId))
      ),
      isSubmitted: submitted,
      rockQuarterOptions: this.rockQuarterOptions,
    }))
  );

  private subscriptions?: Subscription;
  private readonly _now = new Date();
  private _dateMax?: Date;

  init() {
    this.resetForm();

    this.setToolState();

    this.subscribe();
  }

  destroy() {
    this.unsubscribe();
  }

  getAdditionalTeams(teamList: TeamListModel[]) {
    const teamId = this.form.get('teamId')?.value;
    const additionalTeamIds = this.form.get('additionalTeamIds')?.value ?? [];
    this.form.patchValue({ additionalTeamIds: additionalTeamIds.filter(id => id !== teamId) });
    return teamList.filter(team => team._id !== this.form.get('teamId')?.value);
  }

  createRock(attachments?: AttachmentsComponent) {
    if (this.form.invalid) {
      this.store.dispatch(UniversalCreateActions.submitted());
      this.isSubmitted$.next(true);
      return;
    }

    this.store.dispatch(UniversalCreateActions.isCreating({ creating: true }));
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
      this.subscriptions = new Subscription();
    }

    const [dispatchSource, toastMessage] = ['CreateRockStore', `${this.getLanguage()?.item} created!`];

    const subscription = combineLatest([this.currentUser$, this.vm$])
      .pipe(
        take(1),
        filter(([user]) => !!user?.company?._id),
        map(([user, { language }]) => {
          const { item } = language;
          this.store.dispatch(UniversalCreateActions.creationMessage({ creationMessage: `Creating ${item}...` }));
          return this.getFormAsRequest(user as UserViewModel);
        }),
        switchMap(request => {
          return this.rocksApi.create(request).pipe(
            catchError(error => {
              this.store.dispatch(UniversalCreateActions.error({ error }));
              return EMPTY;
            })
          );
        }),
        tap(response => {
          // Check if attachments exist to determine if we should skip auto-close
          const hasAttachments = attachments?.hasDeferredAttachments() ?? false;
          this.store.dispatch(
            RockActions.universalCreateSuccess({
              rock: response[0],
              dispatchSource,
              toastMessage,
              skipAutoClose: hasAttachments,
            })
          );
        }),
        catchError(error => {
          this.store.dispatch(UniversalCreateActions.error({ error }));
          return EMPTY;
        })
      )
      .subscribe({
        next: response => {
          this.store.dispatch(UniversalCreateActions.isCreating({ creating: false }));

          // Only close immediately if no rock was created OR no deferred attachments exist
          if (!response?.length || !attachments?.hasDeferredAttachments()) {
            this.closeUniversalCreate();
            return;
          }

          // Process deferred attachments
          this.store.dispatch(
            UniversalCreateActions.processingAttachments({
              processing: true,
              creationMessage: 'Attaching Files...',
            })
          );

          attachments?.attachDeferredFiles({
            parentId: response[0]._id,
            parentType: AttachmentParentType.Rock,
            closeUniversalCreate: true,
            completionToastMessage: toastMessage,
            dispatchSource,
          });
        },
      });
    this.subscriptions?.add(subscription);
  }

  public onTeamIdChange(teamId: string): void {
    combineLatest([this.users$])
      .pipe(take(1))
      .subscribe(([users]) => {
        this.teamUsers = users.filter(user => user.teamIds.includes(teamId));
        if (!this.teamUsers.some(user => user._id === this.form.get('userId')?.value)) {
          this.form.get('userId')?.setValue('');
        }
      });
  }

  private closeUniversalCreate() {
    this.store.dispatch(UniversalCreateActions.close({ dispatchSource: 'CreateRockStore' }));
  }

  private resetForm(): void {
    this.form.patchValue({
      ...InitialFormValues,
      // dueDate: InitialFormValues.dueDate,
    });
    this.isSubmitted$.next(false);
  }

  private subscribe() {
    this.unsubscribe();

    this.add(
      this.form.statusChanges
        .pipe(
          startWith('INVALID' as FormControlStatus),
          distinctUntilChanged(),
          map(status => status === 'VALID')
        )
        .subscribe(enable => this.store.dispatch(UniversalCreateActions.enable({ enable })))
    );

    this.add(
      this.store
        .select(this.selectRockUniversalCreateButtonText)
        .pipe(distinctUntilChanged())
        .subscribe(createButtonText =>
          this.store.dispatch(UniversalCreateActions.createButtonText({ createButtonText }))
        )
    );

    this.add(
      combineLatest([
        this.form.controls.description.valueChanges.pipe(startWith(this.form.controls.description.value)),
        this.form.controls.teamId.valueChanges.pipe(startWith(this.form.controls.teamId.value)),
        this.form.controls.userId.valueChanges.pipe(startWith(this.form.controls.userId.value)),
        this.form.controls.title.valueChanges.pipe(startWith(this.form.controls.title.value)),
      ])
        .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(([description, teamId, userId, title]) => {
          this.store.dispatch(
            UniversalCreateActions.updateToolState({
              feature: 'rock',
              state: { description: description ?? '', teamId: teamId ?? '', userId: userId ?? '', title: title ?? '' },
            })
          );
        })
    );
  }

  private add(subscription: Subscription): void {
    this.subscriptions ??= new Subscription();
    this.subscriptions.add(subscription);
  }

  private unsubscribe() {
    this.subscriptions?.unsubscribe();
    this.subscriptions = undefined;
  }

  private getLanguage(): RockLanguage | undefined {
    let language: RockLanguage | undefined;
    this.vm$.pipe(take(1)).subscribe(vm => (language = vm.language));
    return language;
  }

  private getDateFormatOptions(): { date: Intl.DateTimeFormatOptions } {
    const format = { date: { month: 'short', day: '2-digit' } as Intl.DateTimeFormatOptions };

    const selectedDateAsString = this.form.controls.dueDate.value;
    if (!selectedDateAsString) return format;

    const selectedDate = new Date(selectedDateAsString);
    if (!selectedDate || isSameYear(this._now, selectedDate)) return format;

    format.date.year = 'numeric';
    return format;
  }

  private getDateMax(): string {
    this._dateMax ??= addYears(this._now, 3);
    return this._dateMax.toISOString();
  }

  private setLevelCode(): RockLevelCode {
    const { isCompanyRock, isDepartmentalRock } = this.form.value;
    if (isCompanyRock && isDepartmentalRock) return RockLevelCode.companyAndDepartment;
    if (isCompanyRock) return RockLevelCode.company;
    if (isDepartmentalRock) return RockLevelCode.department;
    return RockLevelCode.user; // Default return value
  }

  private getFormAsRequest(user: UserViewModel): CreateRockRequestModel {
    const selectedQuarter = (this.form.controls.quarter.value as RockQuarterSelection).split('-')[0] as RockQuarter;
    const rockQuarterYearDueDate =
      this.rockQuarterOptions.find(opt => opt.quarter === selectedQuarter)?.rockQuarterYearDueDate ?? new Date();

    const timezone = extractValueFromStore(this.store, CurrentUserSelectors.selectCurrentUserEffectiveTimezone);
    const dueDate = setDueDateTime(timezone, new Date(this.form.controls.dueDate.value ?? new Date()));

    return {
      userIds: [user._id as string],
      rock: {
        userId: user._id as string,
        title: this.form.controls.title.value ?? '',
        description: this.form.controls.description.value ?? '',
        dueDate,
        companyId: user.company?._id as string,
        teamId: this.form.controls.teamId.value as string,
        additionalTeamIds: this.form.controls.additionalTeamIds.value as string[],
        originalDueDate: dueDate,
        createdBy: getUserName(user),
        statusCode: this.form.controls.statusCode.value as RockStatusCode,
        levelCode: this.setLevelCode(),
        quarter: selectedQuarter ?? RockQuarter.none,
        rockQuarterYearDueDate,
      },
      addCreatorToFollowersList: true,
    };
  }

  private setRockQuarterOptions(): { quarter: RockQuarter; rockQuarterYearDueDate: Date }[] {
    const fc = this.fiscalCalendar;

    const current = fc.resolveCurrentQuarter();
    const previous = fc.resolvePreviousQuarter();
    const quarters = fc.nextNQuarters(previous, 4);

    this.rockQuarterOptions = [
      { quarter: RockQuarter.none, year: new Date().getFullYear(), rockQuarterYearDueDate: new Date() },
    ];

    this.rockQuarterOptions.push(
      ...[...quarters].map(q => ({
        quarter: `Q${q.value}` as RockQuarter,
        year: q.fiscalYear,
        rockQuarterYearDueDate: q.start,
      }))
    );

    const currentQuarter = this.rockQuarterOptions.find(
      q => q.quarter === (`Q${current.value}` as RockQuarter) && q.year === current.fiscalYear
    );

    if (currentQuarter) {
      const quarter = `Q${current.value}-${current.fiscalYear}` as RockQuarterSelection;
      const rockQuarterYearDueDate = currentQuarter.rockQuarterYearDueDate;
      this.form.patchValue({
        quarter,
        rockQuarterYearDueDate,
      });
    }

    return this.rockQuarterOptions;
  }

  private setToolState() {
    const toolState = extractValueFromStore(this.store, selectRockToolState);
    const currentUserId = extractValueFromStore(this.store, CurrentUserSelectors.selectId);

    applyToolStateToForm(this.form, toolState, {
      userId: currentUserId,
    });

    this.setDefaultDueDate();
    this.setRockQuarterOptions();
  }

  private setDefaultDueDate() {
    const defaultDueDate = addDays(new Date(), 90);
    this.form.patchValue({ dueDate: defaultDueDate.toISOString() });
  }
}
